<template>
  <div class="mfa-code-input">
    <div class="d-flex justify-content-start mb-6">
      <input
        v-for="(char, index) in code"
        :key="index"
        :ref="`code${index}`"
        class="form-control code-input text-center me-2"
        type="text"
        :disabled="submitting"
        :required="true"
        v-model="code[index]"
        @keyup.enter="submit"
        @input="onInput(index, $event)"
        @change="onInput(index, $event)"
        @blur="v$.code.$touch()"
        @keyup.backspace="onBackspace(index, $event)"
        @paste="onPaste(index, $event)"
        pattern="[0-9]"
        autocomplete="one-time-code"
        inputmode="numeric"
      />
    </div>
    <div class="form-check" v-if="dontChallengeOption">
      <input
        type="checkbox"
        class="form-check-input"
        @click="toggleRememberDevice()"
        :disabled="submitting"
      />
      <label class="form-check-label" for="checkbox1">Don't challenge me for 7 days</label>
    </div>
    <div class="resend-code d-flex justify-content-between align-items-center row mt-4">
      <div class="col-12 col-sm-6">
        <div class="fw-bold mb-1">Didn't receive your code?</div>
        <div>
          <p v-if="seconds > 0">You can resend a code in {{seconds}} seconds.</p>
          <a v-else class="fw-bold" @click="resendCode">Resend code</a>
        </div>
      </div>
      <div class="col-12 col-sm-6 text-end verify-button">
        <button
          class="btn btn-primary"
          :disabled="submitting || safeGet(v$, 'code.$invalid') || !safeGet(v$, 'code.$dirty') || isCodeInvalid"
          @click="submit">
          <ButtonLoadingSpinner v-if="submitting"></ButtonLoadingSpinner>
          Verify
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useVuelidate } from "@vuelidate/core";
import { required, numeric } from "@vuelidate/validators";
import get from "lodash/get";
import ButtonLoadingSpinner from "~/components/marketplace/subComponents/ButtonLoadingSpinner.vue";

export default {
  name: 'MfaCodeInput',
  components: [
    ButtonLoadingSpinner,
  ],
  props: {
    dontChallengeOption: {
      type: Boolean,
      default: false,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
  },
  async setup() {
    return {
      v$: useVuelidate(),
      safeGet: get,
    };
  },
  data() {
    return {
      code: ['', '', '', '', '', ''],
      rememberDevice: false,
      seconds: 30,
      intervalId: null,
    };
  },
  validations() {
    return {
      code: {
        $each: {
          required,
          numeric,
        },
      },
    };
  },
  mounted() {
    this.startTimer();
    this.$refs[`code0`][0]?.focus();
  },
  beforeUnmount() {
    this.clearTimer();
  },
  computed: {
    isCodeInvalid() {
      return this.code.some((char) => char === '');
    },
  },
  methods: {
    onInput(index, $event) {
      this.v$.code.$touch()
      if (this.code[index] === '' || !$event.target.value) {
        this.code[index] = '';
        return;
      }
      if (isNaN($event.target.value)) {
        this.code[index] = '';
        return;
      }
      if ($event?.target?.value?.length > 2) {
        this.onPaste(index, $event, $event?.target?.value);
      } else {
        this.code[index] = $event.target.value[$event.target.value.length - 1];
        if (index < 5) {
          this.$refs[`code${index + 1}`][0]?.focus();
        }
      }
    },
    onPaste(index, $event, override = '') {
      const pastedData = override || $event.clipboardData.getData('text');
      if (pastedData.length === 6) {
        index = 0;
      }
      if (pastedData.length + index > 6) {
        $event.preventDefault();
        return;
      }
      for (let i = 0; i < pastedData.length; i++) {
        if (isNaN(pastedData[i])) {
          $event.preventDefault();
          return;
        }
        this.code[index + i] = pastedData[i];
      }
      this.code.forEach((_, i) => {
        this.$refs[`code${i}`][0]?.blur();
      });
    },
    onBackspace(index, $event) {
      if (index > 0 && this.code[index] === '') {
        $event.target.previousElementSibling.focus()
      }
    },
    submit() {
      this.v$.code.$touch();
      if (!this.v$.code.$invalid && !this.isCodeInvalid) {
        this.$emit(
          'submit',
          this.dontChallengeOption
            ? { code: this.code.join(''), rememberDevice: this.rememberDevice }
            : { code: this.code.join('') }
        );
      }
    },
    resendCode() {
      this.resetTimer();
      this.$emit('resend');
    },
    toggleRememberDevice() {
      this.rememberDevice = !this.rememberDevice;
    },
    startTimer() {
      clearInterval(this.intervalId);
      this.intervalId = setInterval(() => {
        if (this.seconds > 0) {
          this.seconds--;
        } else {
          this.clearTimer();
        }
      }, 1000);
    },
    resetTimer() {
      this.seconds = 30;
      this.startTimer();
    },
    clearTimer() {
      clearInterval(this.intervalId);
    },
  },
};
</script>

<style lang="scss" scoped>
.resend-code {
  font-size: 14px;
}
.mfa-code-input {
  margin-bottom: -16px;
}
.code-inputs {
  margin: 0 auto;
}
.code-input {
  max-width: 44px;
}
@media screen and (max-width: 360px) {
  .verify-button {
    margin-top: 24px;
  }
}
</style>
